import {
  CanvasTexture,
  LinearFilter,
  LinearMipmapLinearFilter,
  RepeatWrapping,
  SRGBColorSpace,
  NoColorSpace,
} from 'three';
import type { Texture } from 'three';

/** Small deterministic PRNG (mulberry32). */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tileable fractal value noise in [0, 1]. */
export function fbm(
  size: number,
  basePeriod: number,
  octaves: number,
  seed: number,
  persistence = 0.5,
): Float32Array {
  const out = new Float32Array(size * size);
  let amplitude = 1;
  let total = 0;
  let period = basePeriod;
  const random = rng(seed);
  for (let o = 0; o < octaves; o += 1) {
    const lattice = new Float32Array(period * period);
    for (let i = 0; i < lattice.length; i += 1) lattice[i] = random();
    for (let y = 0; y < size; y += 1) {
      const fy = (y / size) * period;
      const y0 = Math.floor(fy);
      const ty = fy - y0;
      const sy = ty * ty * (3 - 2 * ty);
      const y1 = (y0 + 1) % period;
      for (let x = 0; x < size; x += 1) {
        const fx = (x / size) * period;
        const x0 = Math.floor(fx);
        const tx = fx - x0;
        const sx = tx * tx * (3 - 2 * tx);
        const x1 = (x0 + 1) % period;
        const a = lattice[y0 * period + x0]!;
        const b = lattice[y0 * period + x1]!;
        const c = lattice[y1 * period + x0]!;
        const d = lattice[y1 * period + x1]!;
        out[y * size + x]! +=
          (a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy) * amplitude;
      }
    }
    total += amplitude;
    amplitude *= persistence;
    period *= 2;
  }
  for (let i = 0; i < out.length; i += 1) out[i]! /= total;
  return out;
}

function canvas(
  width: number,
  height = width,
): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d')!;
  return { c, ctx };
}

function finish(c: HTMLCanvasElement, srgb: boolean, worldSize: number): Texture {
  const texture = new CanvasTexture(c);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = srgb ? SRGBColorSpace : NoColorSpace;
  texture.repeat.set(1 / worldSize, 1 / worldSize);
  texture.anisotropy = 8;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.magFilter = LinearFilter;
  texture.generateMipmaps = true;
  return texture;
}

export interface SurfaceMaps {
  map: Texture;
  normalMap: Texture;
  roughnessMap?: Texture;
}

interface SurfaceSpec {
  size: number;
  worldSize: number;
  seed: number;
  /** Height in roughly [0, 1] for normal generation. */
  height: (x: number, y: number) => number;
  /** sRGB colour for a pixel. */
  color: (x: number, y: number, h: number) => [number, number, number];
  normalStrength: number;
  /** Extra painting on top of the colour canvas. */
  paint?: (ctx: CanvasRenderingContext2D, random: () => number) => void;
  roughness?: (x: number, y: number, h: number) => number;
}

function surface(spec: SurfaceSpec): SurfaceMaps {
  const { size } = spec;
  const heights = new Float32Array(size * size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) heights[y * size + x] = spec.height(x, y);
  }
  const color = canvas(size);
  const image = color.ctx.createImageData(size, size);
  const rough = spec.roughness ? canvas(size) : null;
  const roughImage = rough ? rough.ctx.createImageData(size, size) : null;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const i = y * size + x;
      const [r, g, b] = spec.color(x, y, heights[i]!);
      image.data[i * 4] = r;
      image.data[i * 4 + 1] = g;
      image.data[i * 4 + 2] = b;
      image.data[i * 4 + 3] = 255;
      if (roughImage && spec.roughness) {
        const v = Math.max(0, Math.min(255, spec.roughness(x, y, heights[i]!) * 255));
        roughImage.data[i * 4] = v;
        roughImage.data[i * 4 + 1] = v;
        roughImage.data[i * 4 + 2] = v;
        roughImage.data[i * 4 + 3] = 255;
      }
    }
  }
  color.ctx.putImageData(image, 0, 0);
  spec.paint?.(color.ctx, rng(spec.seed + 99));
  if (rough && roughImage) rough.ctx.putImageData(roughImage, 0, 0);

  const normal = canvas(size);
  const normalImage = normal.ctx.createImageData(size, size);
  const k = spec.normalStrength;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const l = heights[y * size + ((x - 1 + size) % size)]!;
      const r = heights[y * size + ((x + 1) % size)]!;
      const u = heights[((y - 1 + size) % size) * size + x]!;
      const d = heights[((y + 1) % size) * size + x]!;
      let nx = (l - r) * k;
      let ny = (d - u) * k;
      let nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len;
      ny /= len;
      nz /= len;
      const i = (y * size + x) * 4;
      normalImage.data[i] = (nx * 0.5 + 0.5) * 255;
      normalImage.data[i + 1] = (ny * 0.5 + 0.5) * 255;
      normalImage.data[i + 2] = (nz * 0.5 + 0.5) * 255;
      normalImage.data[i + 3] = 255;
    }
  }
  normal.ctx.putImageData(normalImage, 0, 0);
  const maps: SurfaceMaps = {
    map: finish(color.c, true, spec.worldSize),
    normalMap: finish(normal.c, false, spec.worldSize),
  };
  if (rough) maps.roughnessMap = finish(rough.c, false, spec.worldSize);
  return maps;
}

function clamp255(v: number): number {
  return Math.max(0, Math.min(255, v));
}

function mix(base: [number, number, number], shift: number): [number, number, number] {
  return [clamp255(base[0] + shift), clamp255(base[1] + shift), clamp255(base[2] + shift)];
}

export function concreteMaps(
  tint: [number, number, number] = [192, 180, 162],
  seed = 1,
  worldSize = 4,
): SurfaceMaps {
  const size = 512;
  const coarse = fbm(size, 4, 4, seed);
  const fine = fbm(size, 64, 3, seed + 1);
  const stains = fbm(size, 3, 3, seed + 2);
  return surface({
    size,
    worldSize,
    seed,
    normalStrength: 3,
    height: (x, y) => {
      const i = y * size + x;
      const joint = x < 2 || y < 2 ? -0.6 : 0;
      return fine[i]! * 0.25 + coarse[i]! * 0.15 + joint;
    },
    color: (x, y) => {
      const i = y * size + x;
      let shift = (coarse[i]! - 0.5) * 24 + (fine[i]! - 0.5) * 12;
      if (stains[i]! > 0.62) shift -= (stains[i]! - 0.62) * 120;
      if (x < 2 || y < 2) shift -= 55;
      return mix(tint, shift);
    },
    roughness: (x, y) => 0.78 + fine[y * size + x]! * 0.2,
    paint: (ctx, random) => {
      ctx.strokeStyle = 'rgba(40,36,32,0.35)';
      ctx.lineWidth = 1;
      for (let c = 0; c < 3; c += 1) {
        let px = random() * size;
        let py = random() * size;
        ctx.beginPath();
        ctx.moveTo(px, py);
        for (let s = 0; s < 18; s += 1) {
          px += (random() - 0.5) * 24;
          py += (random() - 0.5) * 24;
          ctx.lineTo(px, py);
        }
        ctx.stroke();
      }
      for (let d = 0; d < 900; d += 1) {
        ctx.fillStyle = random() < 0.5 ? 'rgba(30,30,30,0.25)' : 'rgba(255,255,255,0.18)';
        ctx.fillRect(random() * size, random() * size, 1.5, 1.5);
      }
    },
  });
}

export function paverMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 32, 3, 11);
  const tones = rng(12);
  const toneGrid: number[] = [];
  for (let i = 0; i < 16; i += 1) toneGrid.push((tones() - 0.5) * 26);
  const tile = size / 4;
  return surface({
    size,
    worldSize: 6,
    seed: 13,
    normalStrength: 4,
    height: (x, y) => {
      const gx = x % tile;
      const gy = y % tile;
      const gap = gx < 2 || gy < 2 ? -1 : 0;
      return noise[y * size + x]! * 0.3 + gap;
    },
    color: (x, y) => {
      const gx = x % tile;
      const gy = y % tile;
      if (gx < 2 || gy < 2) return [128, 112, 96];
      const tone = toneGrid[Math.floor(y / tile) * 4 + Math.floor(x / tile)]!;
      const n = noise[y * size + x]!;
      return [
        clamp255(214 + tone + (n - 0.5) * 30),
        clamp255(188 + tone * 0.9 + (n - 0.5) * 26),
        clamp255(152 + tone * 0.7 + (n - 0.5) * 20),
      ];
    },
    roughness: (x, y) => 0.7 + noise[y * size + x]! * 0.25,
  });
}

export function asphaltMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 8, 4, 21);
  const grit = fbm(size, 128, 1, 22);
  return surface({
    size,
    worldSize: 6,
    seed: 23,
    normalStrength: 5,
    height: (x, y) => grit[y * size + x]! * 0.6 + noise[y * size + x]! * 0.2,
    color: (x, y) => {
      const i = y * size + x;
      const g = grit[i]!;
      let shift = (noise[i]! - 0.5) * 22 + (g - 0.5) * 30;
      if (g > 0.8) shift += 40;
      return mix([66, 66, 70], shift);
    },
    roughness: (x, y) => 0.85 + grit[y * size + x]! * 0.15,
  });
}

export function grassMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 6, 4, 31);
  const blades = fbm(size, 128, 2, 32);
  return surface({
    size,
    worldSize: 6,
    seed: 33,
    normalStrength: 6,
    height: (x, y) => blades[y * size + x]!,
    color: (x, y) => {
      const i = y * size + x;
      const n = noise[i]!;
      const b = blades[i]!;
      return [
        clamp255(58 + n * 40 + b * 30),
        clamp255(96 + n * 50 + b * 50),
        clamp255(38 + n * 18),
      ];
    },
    roughness: () => 0.95,
  });
}

export function dirtMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 6, 5, 41);
  const pebble = fbm(size, 96, 1, 42);
  return surface({
    size,
    worldSize: 5,
    seed: 43,
    normalStrength: 6,
    height: (x, y) => noise[y * size + x]! * 0.6 + (pebble[y * size + x]! > 0.78 ? 0.6 : 0),
    color: (x, y) => {
      const i = y * size + x;
      let shift = (noise[i]! - 0.5) * 50;
      if (pebble[i]! > 0.78) shift += 35;
      return mix([150, 104, 66], shift);
    },
    roughness: () => 0.97,
  });
}

export function plasterMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 5, 5, 51);
  const fine = fbm(size, 96, 2, 52);
  return surface({
    size,
    worldSize: 5,
    seed: 53,
    normalStrength: 1.6,
    height: (x, y) => fine[y * size + x]! * 0.5,
    color: (x, y) => {
      const i = y * size + x;
      const n = noise[i]!;
      const stain = n > 0.66 ? (n - 0.66) * 110 : 0;
      return [
        clamp255(168 + (fine[i]! - 0.5) * 14 - stain),
        clamp255(200 + (fine[i]! - 0.5) * 12 - stain * 0.9),
        clamp255(214 + (fine[i]! - 0.5) * 10 - stain * 0.7),
      ];
    },
    roughness: (x, y) => 0.55 + fine[y * size + x]! * 0.2,
  });
}

export function tileMaps(): SurfaceMaps {
  const size = 512;
  const tiles = 16;
  const cell = size / tiles;
  const random = rng(61);
  const tone: number[] = [];
  for (let i = 0; i < tiles * tiles; i += 1) tone.push(random());
  return surface({
    size,
    worldSize: 0.5,
    seed: 62,
    normalStrength: 3,
    height: (x, y) => (x % cell < 2 || y % cell < 2 ? 0 : 1),
    color: (x, y) => {
      if (x % cell < 2 || y % cell < 2) return [226, 226, 218];
      const t = tone[Math.floor(y / cell) * tiles + Math.floor(x / cell)]!;
      return [clamp255(22 + t * 30), clamp255(96 + t * 50), clamp255(168 + t * 50)];
    },
    roughness: (x, y) => (x % cell < 2 || y % cell < 2 ? 0.8 : 0.18),
  });
}

export function rampMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 8, 4, 71);
  const seamEvery = size / 2;
  return surface({
    size,
    worldSize: 2.44,
    seed: 72,
    normalStrength: 2,
    height: (x, y) =>
      noise[y * size + x]! * 0.2 + (y % seamEvery < 2 ? -0.8 : 0) + (x % seamEvery < 2 ? -0.8 : 0),
    color: (x, y) => {
      if (y % seamEvery < 2 || x % seamEvery < 2) return [40, 36, 34];
      const n = noise[y * size + x]!;
      return [
        clamp255(86 + (n - 0.5) * 20),
        clamp255(80 + (n - 0.5) * 18),
        clamp255(78 + (n - 0.5) * 16),
      ];
    },
    roughness: (x, y) => 0.42 + noise[y * size + x]! * 0.3,
    paint: (ctx, random) => {
      // Scuffs and wheel marks.
      for (let s = 0; s < 40; s += 1) {
        ctx.strokeStyle = `rgba(20,18,18,${0.08 + random() * 0.12})`;
        ctx.lineWidth = 1 + random() * 3;
        ctx.beginPath();
        const x = random() * size;
        const y = random() * size;
        ctx.moveTo(x, y);
        ctx.quadraticCurveTo(
          x + (random() - 0.5) * 200,
          y + (random() - 0.5) * 80,
          x + (random() - 0.5) * 300,
          y + (random() - 0.5) * 60,
        );
        ctx.stroke();
      }
    },
  });
}

const STICKER_COLORS = [
  '#ff4d3d',
  '#ffd23f',
  '#2ec4ff',
  '#7cff4f',
  '#ff5fd2',
  '#ffffff',
  '#111111',
  '#ff9a2e',
];

export function plywoodMaps(): SurfaceMaps {
  const size = 512;
  const grain = fbm(size, 4, 4, 81);
  const streak = fbm(size, 64, 2, 82);
  return surface({
    size,
    worldSize: 2.44,
    seed: 83,
    normalStrength: 1.5,
    height: (x, y) =>
      Math.sin((x + grain[y * size + x]! * 90) * 0.18) * 0.2 + streak[y * size + x]! * 0.2,
    color: (x, y) => {
      const i = y * size + x;
      const g = Math.sin((x + grain[i]! * 90) * 0.18) * 0.5 + 0.5;
      return [
        clamp255(176 + g * 22 - streak[i]! * 20),
        clamp255(134 + g * 18 - streak[i]! * 18),
        clamp255(88 + g * 10 - streak[i]! * 12),
      ];
    },
    roughness: () => 0.8,
    paint: (ctx, random) => {
      for (let s = 0; s < 14; s += 1) {
        const x = random() * size;
        const y = random() * size;
        const w = 20 + random() * 60;
        const h = 12 + random() * 36;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((random() - 0.5) * 0.8);
        ctx.fillStyle = STICKER_COLORS[Math.floor(random() * STICKER_COLORS.length)]!;
        if (random() < 0.4) {
          ctx.beginPath();
          ctx.arc(0, 0, h / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-w / 2, -h / 2, w, h);
        }
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(-w / 3, -2, (w * 2) / 3, 4);
        ctx.restore();
      }
    },
  });
}

export function plankMaps(tint: [number, number, number] = [150, 108, 70], seed = 91): SurfaceMaps {
  const size = 512;
  const grain = fbm(size, 8, 3, seed);
  const boards = 8;
  const board = size / boards;
  const random = rng(seed + 1);
  const tones: number[] = [];
  for (let i = 0; i < boards; i += 1) tones.push((random() - 0.5) * 30);
  return surface({
    size,
    worldSize: 2.4,
    seed: seed + 2,
    normalStrength: 3,
    height: (x, y) => (y % board < 3 ? -1 : 0) + grain[y * size + x]! * 0.3,
    color: (x, y) => {
      if (y % board < 3) return [40, 30, 22];
      const t = tones[Math.floor(y / board)]!;
      const g = Math.sin(x * 0.05 + grain[y * size + x]! * 12) * 10;
      return mix(tint, t + g);
    },
    roughness: () => 0.7,
  });
}

export function graniteMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 16, 3, 101);
  const specks = fbm(size, 160, 1, 102);
  return surface({
    size,
    worldSize: 2,
    seed: 103,
    normalStrength: 1,
    height: (x, y) => noise[y * size + x]! * 0.3,
    color: (x, y) => {
      const i = y * size + x;
      const s = specks[i]!;
      let shift = (noise[i]! - 0.5) * 20;
      if (s > 0.8) shift -= 60;
      else if (s < 0.2) shift += 50;
      return mix([132, 130, 128], shift);
    },
    roughness: (x, y) => 0.35 + noise[y * size + x]! * 0.2,
  });
}

export function brickMaps(): SurfaceMaps {
  const size = 512;
  const noise = fbm(size, 16, 3, 111);
  const rows = 16;
  const rowH = size / rows;
  const brickW = rowH * 2.2;
  const random = rng(112);
  const tones: number[] = [];
  for (let i = 0; i < 256; i += 1) tones.push((random() - 0.5) * 40);
  return surface({
    size,
    worldSize: 2.5,
    seed: 113,
    normalStrength: 4,
    height: (x, y) => {
      const row = Math.floor(y / rowH);
      const offset = row % 2 ? brickW / 2 : 0;
      const bx = (x + offset) % brickW;
      return (y % rowH < 3 || bx < 3 ? -1 : 0) + noise[y * size + x]! * 0.3;
    },
    color: (x, y) => {
      const row = Math.floor(y / rowH);
      const offset = row % 2 ? brickW / 2 : 0;
      const bx = (x + offset) % brickW;
      if (y % rowH < 3 || bx < 3) return [150, 146, 136];
      const id = (row * 7 + Math.floor((x + offset) / brickW)) % 256;
      return mix([150, 68, 48], tones[id]! + (noise[y * size + x]! - 0.5) * 30);
    },
    roughness: () => 0.9,
  });
}

export function containerMaps(tint: [number, number, number], seed: number): SurfaceMaps {
  const size = 512;
  const rust = fbm(size, 6, 4, seed);
  const ribs = 24;
  return surface({
    size,
    worldSize: 2.6,
    seed: seed + 1,
    normalStrength: 5,
    height: (x) => Math.abs(Math.sin((x / size) * Math.PI * ribs)),
    color: (x, y) => {
      const i = y * size + x;
      const r = rust[i]!;
      const rib = Math.abs(Math.sin((x / size) * Math.PI * ribs));
      let c = mix(tint, (rib - 0.5) * 30);
      if (r > 0.64) {
        const t = Math.min(1, (r - 0.64) * 4);
        c = [c[0] * (1 - t) + 120 * t, c[1] * (1 - t) + 62 * t, c[2] * (1 - t) + 30 * t];
      }
      return c;
    },
    roughness: (x, y) => 0.5 + rust[y * size + x]! * 0.4,
  });
}

/** Original, code-drawn graffiti piece on a transparent background. */
export function graffitiTexture(seed: number, aspect: number): Texture {
  const height = 256;
  const width = Math.min(2048, Math.max(256, Math.round(height * aspect)));
  const { c, ctx } = canvas(width, height);
  const random = rng(seed);
  const palettes = [
    ['#ff3d6e', '#ffd23f', '#1a1030'],
    ['#2ec4ff', '#b8ff3d', '#101828'],
    ['#ff9a2e', '#a66bff', '#1b1024'],
    ['#7cff4f', '#ff5fd2', '#0f1a12'],
    ['#ffffff', '#ff4d3d', '#141414'],
  ];
  const words = [
    'THRASH',
    'SHRED',
    'GRIND',
    'RIDE',
    'GNAR',
    'RAD',
    'SEND IT',
    'AIR',
    'CARVE',
    'DROP IN',
    'BURLY',
    'STOKED',
  ];
  const pieces = Math.max(1, Math.floor(aspect / 2.6));
  const slot = width / pieces;
  for (let p = 0; p < pieces; p += 1) {
    const palette = palettes[Math.floor(random() * palettes.length)]!;
    const word = words[Math.floor(random() * words.length)]!;
    const cx = slot * (p + 0.5) + (random() - 0.5) * slot * 0.15;
    const cy = height * (0.5 + (random() - 0.5) * 0.12);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((random() - 0.5) * 0.18);
    ctx.transform(1, 0, (random() - 0.5) * 0.5, 1, 0, 0);
    const fontSize = Math.min(height * 0.62, (slot * 1.5) / Math.max(3, word.length));
    ctx.font = `900 ${fontSize}px "Arial Black", Impact, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    // Cloud/splat backdrop.
    ctx.fillStyle = palette[2]!;
    for (let b = 0; b < 9; b += 1) {
      ctx.beginPath();
      ctx.arc(
        (random() - 0.5) * slot * 0.8,
        (random() - 0.5) * height * 0.5,
        fontSize * (0.35 + random() * 0.3),
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
    ctx.lineWidth = fontSize * 0.28;
    ctx.strokeStyle = palette[2]!;
    ctx.strokeText(word, 0, 0);
    ctx.lineWidth = fontSize * 0.12;
    ctx.strokeStyle = '#ffffff';
    ctx.strokeText(word, 0, 0);
    const gradient = ctx.createLinearGradient(0, -fontSize / 2, 0, fontSize / 2);
    gradient.addColorStop(0, palette[0]!);
    gradient.addColorStop(1, palette[1]!);
    ctx.fillStyle = gradient;
    ctx.fillText(word, 0, 0);
    // Highlights and drips.
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    for (let h = 0; h < 6; h += 1) {
      ctx.fillRect(
        (random() - 0.5) * slot * 0.6,
        -fontSize * 0.3 + random() * 6,
        fontSize * 0.12,
        fontSize * 0.05,
      );
    }
    ctx.fillStyle = palette[0]!;
    for (let d = 0; d < 7; d += 1) {
      const dx = (random() - 0.5) * slot * 0.7;
      const len = fontSize * (0.2 + random() * 0.5);
      ctx.fillRect(dx, fontSize * 0.25, fontSize * 0.05, len);
      ctx.beginPath();
      ctx.arc(dx + fontSize * 0.025, fontSize * 0.25 + len, fontSize * 0.04, 0, Math.PI * 2);
      ctx.fill();
    }
    // Stars and sparkle accents.
    ctx.fillStyle = palette[1]!;
    for (let s = 0; s < 3; s += 1) {
      const sx = (random() - 0.5) * slot * 0.9;
      const sy = (random() - 0.5) * height * 0.8;
      const r = fontSize * (0.1 + random() * 0.12);
      ctx.beginPath();
      for (let k = 0; k < 10; k += 1) {
        const a = (k / 10) * Math.PI * 2;
        const rr = k % 2 ? r * 0.4 : r;
        ctx.lineTo(sx + Math.cos(a) * rr, sy + Math.sin(a) * rr);
      }
      ctx.fill();
    }
    ctx.restore();
  }
  const texture = new CanvasTexture(c);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}

/** Neon sign: glowing tube lettering on transparent background (use as emissive + alpha). */
export function neonTexture(text: string, color: string): Texture {
  const { c, ctx } = canvas(1024, 192);
  ctx.font = '900 132px "Arial Black", Impact, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = color;
  ctx.shadowBlur = 28;
  ctx.lineWidth = 10;
  ctx.strokeStyle = color;
  for (let i = 0; i < 3; i += 1) ctx.strokeText(text, 512, 100);
  ctx.shadowBlur = 0;
  ctx.lineWidth = 4;
  ctx.strokeStyle = '#ffffff';
  ctx.strokeText(text, 512, 100);
  const texture = new CanvasTexture(c);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/** Building facade with lit windows (emissive). */
export function windowsTexture(seed: number): { map: Texture; emissive: Texture } {
  const { c, ctx } = canvas(256, 512);
  const glow = canvas(256, 512);
  const random = rng(seed);
  ctx.fillStyle = '#1b1d26';
  ctx.fillRect(0, 0, 256, 512);
  glow.ctx.fillStyle = '#000';
  glow.ctx.fillRect(0, 0, 256, 512);
  for (let y = 8; y < 512; y += 22) {
    for (let x = 8; x < 256; x += 20) {
      const lit = random() < 0.16;
      ctx.fillStyle = lit ? '#3a3528' : '#262a36';
      ctx.fillRect(x, y, 12, 14);
      if (lit) {
        const warm = random() < 0.7;
        glow.ctx.fillStyle = warm
          ? `rgba(255,${190 + random() * 40},${110 + random() * 60},1)`
          : 'rgba(170,210,255,1)';
        glow.ctx.fillRect(x, y, 12, 14);
      }
    }
  }
  const map = new CanvasTexture(c);
  map.colorSpace = SRGBColorSpace;
  map.wrapS = RepeatWrapping;
  map.wrapT = RepeatWrapping;
  const emissive = new CanvasTexture(glow.c);
  emissive.colorSpace = SRGBColorSpace;
  emissive.wrapS = RepeatWrapping;
  emissive.wrapT = RepeatWrapping;
  return { map, emissive };
}

/** Soft round sprite for particles, dust, glows and blob shadows. */
export function softDotTexture(): Texture {
  const { c, ctx } = canvas(64);
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.4, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  const texture = new CanvasTexture(c);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/** Diamond chain-link pattern (alpha) for fences. */
export function chainLinkTexture(): Texture {
  const { c, ctx } = canvas(128);
  ctx.clearRect(0, 0, 128, 128);
  ctx.strokeStyle = 'rgba(200,205,210,1)';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(0, 64);
  ctx.lineTo(64, 0);
  ctx.lineTo(128, 64);
  ctx.lineTo(64, 128);
  ctx.closePath();
  ctx.stroke();
  const texture = new CanvasTexture(c);
  texture.colorSpace = SRGBColorSpace;
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.anisotropy = 8;
  return texture;
}

/** Name tag sprite canvas. */
export function nameTagTexture(name: string, color: string): Texture {
  const { c, ctx } = canvas(512, 128);
  ctx.font = '800 56px "Trebuchet MS", "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const width = Math.min(500, ctx.measureText(name).width + 60);
  ctx.fillStyle = 'rgba(8,10,16,0.6)';
  const x = 256 - width / 2;
  ctx.beginPath();
  ctx.roundRect(x, 24, width, 80, 40);
  ctx.fill();
  ctx.fillStyle = color;
  ctx.fillRect(x + 22, 56, 14, 14);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(name, 256 + 12, 66);
  const texture = new CanvasTexture(c);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

/** Worn painted ground art: rings, stripes and arrows in park colours (transparent). */
export function paintTexture(seed: number): Texture {
  const { c, ctx } = canvas(512);
  const random = rng(seed * 7919);
  const colors = ['#ff3d6e', '#ffd23f', '#35d6ff', '#7cff4f', '#ffffff', '#ff9a2e', '#a66bff'];
  const pick = () => colors[Math.floor(random() * colors.length)]!;
  ctx.translate(256, 256);
  const style = seed % 4;
  ctx.lineCap = 'round';
  if (style === 0) {
    for (let r = 0; r < 4; r += 1) {
      ctx.strokeStyle = pick();
      ctx.lineWidth = 18;
      ctx.beginPath();
      ctx.arc(0, 0, 220 - r * 48, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else if (style === 1) {
    for (let i = -4; i <= 4; i += 1) {
      ctx.fillStyle = i % 2 === 0 ? pick() : 'rgba(0,0,0,0)';
      ctx.save();
      ctx.rotate(-0.6);
      ctx.fillRect(i * 52 - 20, -260, 36, 520);
      ctx.restore();
    }
  } else if (style === 2) {
    ctx.fillStyle = pick();
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath();
      ctx.moveTo(-160 + i * 110, -120);
      ctx.lineTo(-60 + i * 110, 0);
      ctx.lineTo(-160 + i * 110, 120);
      ctx.lineTo(-120 + i * 110, 120);
      ctx.lineTo(-20 + i * 110, 0);
      ctx.lineTo(-120 + i * 110, -120);
      ctx.closePath();
      ctx.fill();
    }
  } else {
    const points = 5;
    ctx.fillStyle = pick();
    ctx.beginPath();
    for (let k = 0; k < points * 2; k += 1) {
      const a = (k / (points * 2)) * Math.PI * 2 - Math.PI / 2;
      const r = k % 2 ? 90 : 220;
      ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    }
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = pick();
    ctx.lineWidth = 14;
    ctx.stroke();
  }
  // Wear: knock out speckles so the paint looks ridden over.
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 2500; i += 1) {
    ctx.globalAlpha = random() * 0.8;
    const s = 1 + random() * 5;
    ctx.fillRect(random() * 512, random() * 512, s, s);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  const texture = new CanvasTexture(c);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}
